import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ROWS_PER_PAGE } from "../App";
import TableBasedCrud, { ActionsMenuEntry } from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import useAccomplishment from "../hooks/useAccomplishments";
import { Accomplishment } from "../models/Accomplishment";
import AccomplishmentUtils from "../utils/AccomplishmentUtils";
import { AccomplishmentStatusFilters } from "../utils/enums/filters/AccomplishmentStatusFilters";
import OpsDateUtils from "../utils/OpsDateUtils";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppToolbar } from "../components/AppToolbar";
import { SelectComponent } from "../components/SelectComponent";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import AccomplishmentStatusDialog from "../components/Accomplishment/AccomplishmentStatusDialog";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { trace } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import UserService from "../services/UserService";
import AccomplishmentDetailsDialog from "../components/Accomplishment/AccomplishmentDetailsDialog";
import { useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import useUpdateUrl from "../hooks/useUpdateUrl";
import useTableHeight from "../hooks/useTableHeight";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Checkbox } from "@mui/material";
import { CheckboxComponent } from "../components/CheckboxComponent";
import { ColorUtils } from "../utils/ColorUtils";
import { useStyles as formStyles } from "../components/Main/formStyles";
import { DropdownButtonComponent } from "../components/DropdownButtonComponent";
import accomplishmentDetailsDialog from "../components/Accomplishment/AccomplishmentDetailsDialog";

const BatchActions = [
  { label: 'Approve batch', color: 'success' },
  { label: 'Reject batch', color: 'error' },
];


const AccomplishmentApproval = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [accomplishment, setAccomplishment] = useState<Accomplishment | null>();
  const [isChangingStatus, setIsChangingStatus] = useState<Accomplishment | undefined>();
  const [action, setAction] = useState<"Approve" | "Reject" | undefined>();
  const [userExternalId, setUserExternalId] = useState<string>();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const [toModify, setToModify] = useState<Accomplishment[]>([]);
  const [selectAll, setSelectAll] = useState(false)
  const [currentPageAccomplishments, setCurrentPageAccomplishments] = useState([])
  const [pages, setPages] = useState(0)
  const [rows, setRows] = useState(0)

  const { isLoading, statusFilter, setStatusFilter, getAllPaginated, handleUpdateStatus, updateTime } = useAccomplishment();

  const searchParams = new URLSearchParams(useLocation().search);
  const status = searchParams.get("status");
  const {updateUrl} = useUpdateUrl(Routes.ACCOMPLISHMENT_APPROVAL, searchParams);
  const customStyles = formStyles();

  useEffect(() => {
    UserService.findByCoreUserId(AuthService.getUser()?.externalId!)
      .then((response) => setUserExternalId(response.getContentOrThrowError().externalId))
      .catch((error) => trace("Error getting usernames", error));
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== null) {
      setStatusFilter(status);
    } else {
      searchParams.set("status", statusFilter);
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewDetails = (instance: Accomplishment) => {
    setAccomplishment(instance);
    setIsOpen(true);
  };

  const getInfoProvided = (accomplishment: Accomplishment): string => {
    let linkCount: number = 0;
    if (accomplishment.info) linkCount = accomplishment.info.length;
    if (linkCount < 1) {
      return "(none)";
    }
    if (linkCount === 1) {
      return "1 entry";
    }
    return `${linkCount} entries`;
  };

  const getAdditionalActions = (instance: Accomplishment): ActionsMenuEntryIcons[] => {
    const options = [];
    const isNotOwner = instance.reporter?.externalId !== userExternalId;

    if (instance.status === AccomplishmentStatusFilters.PENDING && isNotOwner) {
      const setIsChangingStatusAndAction = (action: "Approve" | "Reject" | undefined) => {
        setIsChangingStatus(instance);
        setAction(action);
      };

      options.push(
        {
          title: "Approve",
          action: () => setIsChangingStatusAndAction("Approve"),
          icon:<CheckCircleOutlineIcon/>
        },
        {
          title: "Reject",
          action: () => setIsChangingStatusAndAction("Reject"),
          icon:<HighlightOffIcon/>
        }
      );
    }

    if (isNotOwner && instance.status !== AccomplishmentStatusFilters.PENDING) {
      options.push({
        title: "Reset status",
        action: () => handleUpdateStatus(instance, "Pending"), icon:<RestartAltIcon/>
      });
    }

    options.push({
      title: t("View Details"),
      action: () => handleViewDetails(instance),
      icon:<VisibilityIcon/>
    });
    return options;
  };

  const handleCheck = (instance: Accomplishment) => {
    const isPresent = toModify.some(obj => obj.externalId === instance.externalId)
    if (!isPresent) {
      const prev = [...toModify, instance]
      setToModify(prev)
      return
    }
    const prev = toModify.filter(obj => obj.externalId !== instance.externalId)
    setToModify(prev)
  }

  const handleSelectAll = () => {
    const selection = !selectAll
    setSelectAll(!selectAll)
    if (selection) {
      let newModifyList = []
      currentPageAccomplishments.forEach(accomp => {
        if (accomp.reporter?.externalId !== userExternalId) {
          newModifyList.push(accomp)
        }
      })
      const addModifyList = [...toModify, ...newModifyList]
      setToModify(addModifyList)
    } else {
      setToModify([]);
    }
  }

  const getData = (data, page, row) => {
    if (page !== pages || row !== rows) {
      setPages(page)
      setRows(row)
      data.then((result) => {
        setCurrentPageAccomplishments(result.content)})
    }
  }

  const closedAccomplishment = accomplishment ? accomplishment.status === AccomplishmentStatusFilters.APPROVED || accomplishment.status === AccomplishmentStatusFilters.REJECTED : false;

  return (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={
          <>
            <AppToolbar title={t("Accomplishment Approval")}>
              <Box
              style={{
                display: 'flex',
                borderRadius: "4px",
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                padding: '1rem',
                border: `${ColorUtils.LIGHTER_GRAY} solid 1px`,
                boxSizing: 'inherit',
                boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)"}}
              >
                <SelectComponent
                  label={"Status"}
                  value={statusFilter}
                  setValue={(value) => {
                    setStatusFilter(value);
                    searchParams.set("status", value);
                    updateUrl();
                  }}
                  options={Object.values(AccomplishmentStatusFilters)}
                  size="large"
                />
                <DropdownButtonComponent
                  label="Modify batch"
                  onClick={(option) => {
                    const statusValue = option.label === 'Approve batch' ? 'Approved' : 'Rejected';
                    toModify.forEach((accomplishmentElement) => {
                      if (accomplishmentElement.reporter?.externalId !== userExternalId) {
                        handleUpdateStatus(accomplishmentElement, statusValue);
                      }
                    });
                    setToModify([]);
                    setSelectAll(false);
                  }}
                  options={BatchActions}
                  size="large"
                  disabled={toModify.length === 0}
                />
                <Box sx={{ ml:2.5, mt:0.5 }}>
                  <CheckboxComponent label="Select all" checked={selectAll} onChange={handleSelectAll} size={20} disabled={currentPageAccomplishments.length === 0}/>
                </Box>
              </Box>
            </AppToolbar>
          </>
        }
        bottomSection={
          <Box sx={{boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px"}}>
            <SpinnerWrapper isLoading={isLoading}>
              <TableBasedCrud<Accomplishment>
                headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
                buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
                cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
                dialogStyles={ customStyles.root }
                maxHeight={calculateMaxHeight(false)}
                stickyHeader
                stickyPagination
                renderDialogContent={() => <></>}
                elementName={t("Accomplishment approval")}
                rowsPerPage={ROWS_PER_PAGE}
                forceUpdateOn={updateTime}
                onDataFetch={(pageNumber, count) => {
                  const paginated = getAllPaginated(pageNumber, count)
                  getData(paginated, pageNumber, count)
                  return paginated
                }}
                denseTable
                disableAutoFetch={false}
                buildAdditionalActions={getAdditionalActions}
                tableColumns={[
                  {
                    header: t(""),
                    content: (instance) => {
                    return(
                      <Checkbox
                        checked={toModify.some(obj => obj.externalId === instance.externalId)}
                        onClick={() => handleCheck(instance)}
                        disabled={instance.reporter?.externalId === userExternalId}
                      />
                    )}
                  },
                  { header: t("Type"), content: (instance) => <>{instance.type.title}</> },
                  {
                    header: t("Performed On"),
                    content: (instance) => <>{OpsDateUtils.ensureFormatDateUTC(instance.reportedOn)}</>,
                  },
                  {
                    header: t("Reporter"),
                    content: (instance) => <>{`${instance.reporter.firstName} ${instance.reporter.lastName}`}</>,
                  },
                  {
                    header: t("Entries"),
                    content: (instance) => <>{getInfoProvided(instance)}</>,
                  },
                  {
                    header: t("Status"),
                    content: (instance) => (
                      <label
                        style={{
                          color: AccomplishmentUtils.getStatusColor(instance.status),
                        }}
                      >
                        {instance.status.replace("_", " ")}
                      </label>
                    ),
                  },
                  {
                    header: "Approved by",
                    content: (instance) => <>{instance.approver ? `${instance.approver.firstName} ${instance.approver.lastName}` : ""}</>,
                  },
                  {
                    header: "Approved on",
                    content: (instance) => <>{OpsDateUtils.ensureFormatDateUTC(instance.approvedDate)}</>,
                  },
                  {
                    header: t("Role"),
                    content: (instance) => <>{instance.role ? instance.role : "No role"}</>,
                  },
                  {
                    header: t("Actions"),
                    content: (instance) => {
                      const actions = getAdditionalActions(instance)
                      return (<>
                        {actions.map((action) => {return (<ActionButton onClick={action.action} tooltip={action.title} icon={action.icon}/>)})}
                        </>
                      )
                    },
                  },
                ]}
                hideActions={true}  // Hide default Actions column
              />
            </SpinnerWrapper>

            <AccomplishmentDetailsDialog isOpen={isOpen} setIsOpen={setIsOpen} accomplishment={accomplishment!} handleUpdateStatus={handleUpdateStatus} hideApproveButton={accomplishment && (accomplishment.reporter.externalId === userExternalId || closedAccomplishment)} />
            {isChangingStatus && (
              <AccomplishmentStatusDialog
                action={action!}
                accomplishment={isChangingStatus}
                onClose={() => setIsChangingStatus(undefined)}
                handleUpdateStatus={(accomplishment, status) => handleUpdateStatus(accomplishment, status)}
              />
            )}
          </Box>
        }
      />
    </>
  );
};

export default AccomplishmentApproval;
